/**
 * Placeholder for the Updates tab feed, so the finished UI can be demonstrated before the
 * backend is wired in.
 *
 * Matches the backend integration document "Updates Feed & Notification Bell"
 * (`POST /api/ActivityFeed/GetFeed`, branch `sprint_87_Notification_rework_BE`) field for
 * field, including its UTC-without-Z timestamp format, so the swap is a flag not a
 * rewrite — and so the UTC parsing path is exercised in the demo.
 *
 * ── Reverting ─────────────────────────────────────────────────────────────────
 * 1. Set `USE_ACTIVITY_FEED_FIXTURE` to false. Every call routes to the real API and the
 *    placeholder branches elsewhere go inert on their own.
 * 2. To remove entirely, delete this file and the two guarded branches that import it —
 *    `api/index.tsx` (getActivityFeed) and `UpdatesTab/index.tsx` (send handlers,
 *    synthetic-id guards). Nothing else references it.
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * All names and content are synthetic. No real patient data belongs in this file.
 */

import {
  ActivityCategory,
  ActivityFeedEntry,
  ActivityFeedPage,
  ActivityFeedRequest,
  ActivityFeedResponse,
} from '../types/activityFeed'

// ─── Demo controls ────────────────────────────────────────────────────────────

/** Master switch. False routes everything to the real API. */
export const USE_ACTIVITY_FEED_FIXTURE = false

/** Which state to render — the API's own empty and failure paths. */
type FixtureScenario = 'populated' | 'empty' | 'error'

// Asserted rather than annotated: TypeScript narrows an annotated const to its
// initializer, which would make the other two branches unreachable code errors.
const FIXTURE_SCENARIO = 'populated' as FixtureScenario

/** Simulated latency, so the loading skeleton is actually visible. */
const FIXTURE_DELAY_MS = 450

/** Entries per page, capped below the caller's page size so paging is demonstrable. */
const FIXTURE_PAGE_SIZE = 8

/**
 * Ids at or above this belong to fixture records. Actions that would hit a real service
 * (opening an attachment, editing a note) check this and explain themselves rather than
 * failing. Replace an id below with a real one to make that action live in a demo.
 */
export const SYNTHETIC_ID_BASE = 900000

/** Channel urls that exist only here; swap for a real thread id to make expand live. */
const SYNTHETIC_CHANNEL_PREFIX = 'fixture_channel_'

const COMMUNITY_A = 'Sunrise Senior Living'
const COMMUNITY_B = 'Golden Years Care Home'
const FACILITY_A = 7734
const FACILITY_B = 7811

/**
 * Timestamp in the API's format: UTC, no `Z` suffix. Anything that renders this must go
 * through `parseApiUtc`, which is exactly what we want the demo to prove.
 */
const minutesAgo = (minutes: number): string =>
  new Date(Date.now() - minutes * 60 * 1000).toISOString().replace('Z', '')

// ─── Feed content ─────────────────────────────────────────────────────────────

/**
 * Newest first — the order GetFeed returns.
 *
 * Covers every case the cards handle: all six emitting categories, referral-wide entries
 * with a null `patientFacilityId`, a Status carrying `metadata.comment`, a denial with a
 * reason, editable and read-only notes, and Activity Log dividers that survive filtering.
 */
const buildEntries = (): ActivityFeedEntry[] => [
  {
    category: ActivityCategory.Note,
    categoryDescription: 'Note',
    sourceId: SYNTHETIC_ID_BASE + 1,
    occurredAt: minutesAgo(8),
    patientFacilityId: null,
    communityName: null,
    actorUserId: 'user-cw',
    actorDisplayName: 'Cameron Williamson',
    title: null,
    body: 'Family has confirmed they are comfortable with the Sunrise placement. Daughter is the primary contact and prefers calls after 5pm.',
    metadata: {
      priority: 'Medium',
      priorityValue: 1,
      engagementTypeId: 4,
      engagementType: 'Family call',
      canEdit: true,
    },
  },
  {
    category: ActivityCategory.Attachment,
    categoryDescription: 'Attachment',
    sourceId: SYNTHETIC_ID_BASE + 2,
    occurredAt: minutesAgo(14),
    patientFacilityId: null,
    communityName: null,
    actorUserId: null,
    actorDisplayName: null,
    title: 'Discharge_Summary.pdf',
    body: 'New attachment added - R12345',
    metadata: {
      attachmentId: SYNTHETIC_ID_BASE + 2,
      fileName: 'Discharge_Summary.pdf',
      fileType: 'pdf',
      size: 481233,
      version: 1,
      description: null,
    },
  },
  {
    category: ActivityCategory.ReferralSource,
    categoryDescription: 'Referral Source',
    sourceId: SYNTHETIC_ID_BASE + 3,
    occurredAt: minutesAgo(25),
    patientFacilityId: FACILITY_A,
    communityName: COMMUNITY_A,
    actorUserId: null,
    actorDisplayName: "St. Mary's General Hospital",
    title: 'Updated discharge summary',
    body: 'Hi, we have attached the updated discharge summary and the latest lab results. Please confirm receipt and let us know if anything further is required for the referral.',
    metadata: {
      isInbound: true,
      communicationType: 1,
      referralSourceId: 3,
      referralSource: 'Fax',
      referringHospital: "St. Mary's General Hospital",
    },
  },
  {
    // The note typed while accepting arrives as metadata.comment on this same entry —
    // there is no separate Comment category.
    category: ActivityCategory.Status,
    categoryDescription: 'Status',
    sourceId: SYNTHETIC_ID_BASE + 4,
    occurredAt: minutesAgo(45),
    patientFacilityId: FACILITY_A,
    communityName: null,
    actorUserId: 'user-mr',
    actorDisplayName: 'Maria Rodriguez',
    title: null,
    body: `Referral status changed to Accepted for Robin Clement - R53456 in ${COMMUNITY_A}`,
    metadata: {
      status: 'Accepted',
      statusId: 6,
      comment: 'Bed confirmed for Thursday admission. Transport arranged through the hospital.',
      denialReason: null,
      denialReasonId: null,
    },
  },
  {
    category: ActivityCategory.Discussion,
    categoryDescription: 'Discussion',
    sourceId: SYNTHETIC_ID_BASE + 5,
    occurredAt: minutesAgo(60),
    patientFacilityId: FACILITY_A,
    communityName: COMMUNITY_A,
    actorUserId: null,
    actorDisplayName: null,
    title: COMMUNITY_A,
    body: null,
    metadata: {
      channelUrl: `${SYNTHETIC_CHANNEL_PREFIX}sunrise`,
      messageCount: 12,
      lastMessageId: 'm-3',
      // Newest first, as the API returns them.
      recentMessages: [
        {
          messageId: 'm-3',
          senderUserId: 'user-sc',
          senderName: 'Dr. Sarah Chen',
          message:
            'Insurance authorization is still pending. I have flagged this with the payor team — expecting an update by end of day.',
          createdDate: minutesAgo(60),
        },
        {
          messageId: 'm-2',
          senderUserId: 'user-ap',
          senderName: 'Aaron Pike',
          message: 'Thanks — I will update the bed board once that is confirmed.',
          createdDate: minutesAgo(72),
        },
        {
          messageId: 'm-1',
          senderUserId: 'user-jd',
          senderName: 'James Doyle',
          message: 'Transport is booked for 10am, confirming with the family now.',
          createdDate: minutesAgo(95),
        },
      ],
    },
  },
  {
    category: ActivityCategory.ActivityLog,
    categoryDescription: 'Activity Log',
    sourceId: SYNTHETIC_ID_BASE + 6,
    occurredAt: minutesAgo(120),
    patientFacilityId: FACILITY_A,
    communityName: null,
    actorUserId: null,
    actorDisplayName: 'j.okafor@sunrise.example',
    title: null,
    body: 'Referral created from Lead L0107',
    metadata: { activityTypeId: 3, activityType: 'Referral', isDivider: true },
  },
  {
    category: ActivityCategory.Status,
    categoryDescription: 'Status',
    sourceId: SYNTHETIC_ID_BASE + 7,
    occurredAt: minutesAgo(180),
    patientFacilityId: FACILITY_B,
    communityName: null,
    actorUserId: 'user-th',
    actorDisplayName: 'Theresa Webb',
    title: null,
    body: `Referral status changed to Denied for Robin Clement - R752458 in ${COMMUNITY_B}`,
    metadata: {
      status: 'Denied',
      statusId: 9,
      comment: 'No suitable bed available in the requested wing for the admission date.',
      denialReason: 'No bed availability',
      denialReasonId: 12,
    },
  },
  {
    category: ActivityCategory.Discussion,
    categoryDescription: 'Discussion',
    sourceId: SYNTHETIC_ID_BASE + 8,
    occurredAt: minutesAgo(240),
    patientFacilityId: FACILITY_B,
    communityName: COMMUNITY_B,
    actorUserId: null,
    actorDisplayName: null,
    title: COMMUNITY_B,
    body: null,
    metadata: {
      channelUrl: `${SYNTHETIC_CHANNEL_PREFIX}golden_years`,
      messageCount: 4,
      lastMessageId: 'm-5',
      recentMessages: [
        {
          messageId: 'm-5',
          senderUserId: 'user-cw',
          senderName: 'Cameron Williamson',
          message: 'Understood — I will look at alternatives and come back to you.',
          createdDate: minutesAgo(240),
        },
        {
          messageId: 'm-4',
          senderUserId: 'user-ln',
          senderName: 'Lena Novak',
          message: 'We are at capacity in the memory care wing through next week.',
          createdDate: minutesAgo(260),
        },
      ],
    },
  },
  {
    category: ActivityCategory.ActivityLog,
    categoryDescription: 'Activity Log',
    sourceId: SYNTHETIC_ID_BASE + 9,
    occurredAt: minutesAgo(300),
    patientFacilityId: null,
    communityName: null,
    actorUserId: null,
    actorDisplayName: null,
    title: null,
    body: 'Clinical review completed',
    metadata: { activityTypeId: 1, activityType: 'Clinical', isDivider: true },
  },
  {
    category: ActivityCategory.Note,
    categoryDescription: 'Note',
    sourceId: SYNTHETIC_ID_BASE + 10,
    occurredAt: minutesAgo(420),
    patientFacilityId: null,
    communityName: null,
    actorUserId: 'user-th',
    actorDisplayName: 'Theresa Webb',
    title: null,
    body: 'Patient prefers a ground-floor room. Noted on the intake call with the discharge planner.',
    metadata: {
      priority: 'Low',
      priorityValue: 0,
      engagementTypeId: 2,
      engagementType: 'Intake call',
      canEdit: false,
    },
  },
  {
    category: ActivityCategory.Attachment,
    categoryDescription: 'Attachment',
    sourceId: SYNTHETIC_ID_BASE + 11,
    occurredAt: minutesAgo(540),
    patientFacilityId: null,
    communityName: null,
    actorUserId: null,
    actorDisplayName: null,
    title: 'Lab_Results.pdf',
    body: 'New attachment added - R12345',
    metadata: {
      attachmentId: SYNTHETIC_ID_BASE + 11,
      fileName: 'Lab_Results.pdf',
      fileType: 'pdf',
      size: 118904,
      version: 2,
      description: 'Latest panel',
    },
  },
  {
    category: ActivityCategory.ReferralSource,
    categoryDescription: 'Referral Source',
    sourceId: SYNTHETIC_ID_BASE + 12,
    occurredAt: minutesAgo(840),
    patientFacilityId: FACILITY_B,
    communityName: COMMUNITY_B,
    actorUserId: null,
    actorDisplayName: 'Hillview Hospital',
    title: 'Equipment requirements',
    body: 'Please confirm whether the community can accommodate a bariatric bed before we proceed with the referral.',
    metadata: {
      isInbound: true,
      communicationType: 1,
      referralSourceId: 2,
      referralSource: 'Email',
      referringHospital: 'Hillview Hospital',
    },
  },
  {
    category: ActivityCategory.ActivityLog,
    categoryDescription: 'Activity Log',
    sourceId: SYNTHETIC_ID_BASE + 13,
    occurredAt: minutesAgo(1200),
    patientFacilityId: FACILITY_A,
    communityName: null,
    actorUserId: null,
    actorDisplayName: null,
    title: null,
    body: 'Insurance verification completed',
    metadata: { activityTypeId: 5, activityType: 'Insurance', isDivider: true },
  },
  {
    category: ActivityCategory.Note,
    categoryDescription: 'Note',
    sourceId: SYNTHETIC_ID_BASE + 14,
    occurredAt: minutesAgo(1440),
    patientFacilityId: null,
    communityName: null,
    actorUserId: 'user-rf',
    actorDisplayName: 'Robert Fields',
    title: null,
    body: 'Discharge planner flagged a same-day admission requirement. Escalating to the community leads.',
    metadata: {
      priority: 'High',
      priorityValue: 2,
      engagementTypeId: 7,
      engagementType: 'Escalation',
      canEdit: false,
    },
  },
  {
    category: ActivityCategory.Status,
    categoryDescription: 'Status',
    sourceId: SYNTHETIC_ID_BASE + 15,
    occurredAt: minutesAgo(2880),
    patientFacilityId: FACILITY_A,
    communityName: null,
    actorUserId: 'user-mr',
    actorDisplayName: 'Maria Rodriguez',
    title: null,
    body: `Referral status changed to Clinical Review Pending for Robin Clement - R53456 in ${COMMUNITY_A}`,
    metadata: {
      status: 'Clinical Review Pending',
      statusId: 3,
      comment: null,
      denialReason: null,
      denialReasonId: null,
    },
  },
  {
    category: ActivityCategory.ActivityLog,
    categoryDescription: 'Activity Log',
    sourceId: SYNTHETIC_ID_BASE + 16,
    occurredAt: minutesAgo(2900),
    patientFacilityId: null,
    communityName: null,
    actorUserId: null,
    actorDisplayName: null,
    title: null,
    body: 'Referral imported from Epic',
    metadata: { activityTypeId: 3, activityType: 'Referral', isDivider: true },
  },
]

// ─── Request handling ─────────────────────────────────────────────────────────

const delay = <T,>(value: T): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), FIXTURE_DELAY_MS)
  })

const buildResponse = (page: ActivityFeedPage): ActivityFeedResponse => ({
  data: {
    isSuccess: true,
    message: 'success',
    totalCount: page.items.length,
    hasData: page.items.length > 0,
    data: page,
  },
})

/**
 * Stands in for `POST /api/ActivityFeed/GetFeed`.
 *
 * Applies the rules the real endpoint does: the category filter narrows the feed but
 * never removes Activity Log entries, and paging is cursor-based.
 * @param body the same request the real endpoint receives.
 */
export const getActivityFeedFixture = (body: ActivityFeedRequest): Promise<ActivityFeedResponse> => {
  if (FIXTURE_SCENARIO === 'error') {
    return delay(null).then(() => {
      throw new Error('Fixture error scenario')
    })
  }

  if (FIXTURE_SCENARIO === 'empty') {
    return delay(buildResponse({ items: [], nextCursor: null, hasMore: false }))
  }

  const all = buildEntries()
  const { categories } = body

  const filtered =
    categories && categories.length
      ? all.filter(
          entry =>
            entry.category === ActivityCategory.ActivityLog || categories.includes(entry.category),
        )
      : all

  const offset = body.cursor ? Number(body.cursor) : 0
  const pageSize = Math.min(body.pageSize > 0 ? body.pageSize : 25, FIXTURE_PAGE_SIZE)
  const items = filtered.slice(offset, offset + pageSize)
  const nextOffset = offset + items.length
  const hasMore = nextOffset < filtered.length

  return delay(
    buildResponse({ items, nextCursor: hasMore ? String(nextOffset) : null, hasMore }),
  )
}

// ─── Action stubs ─────────────────────────────────────────────────────────────

/**
 * Stubs for the actions the feed cards trigger, so a demo exercises the whole UI rather
 * than erroring on the first click.
 *
 * `isSyntheticId` returns false whenever the fixture is off, so the guards that call it
 * cost nothing once the real API is wired.
 */
export const activityFeedPlaceholder = {
  enabled: USE_ACTIVITY_FEED_FIXTURE,

  /** True for records that exist only in this file. */
  isSyntheticId: (id: number): boolean => USE_ACTIVITY_FEED_FIXTURE && id >= SYNTHETIC_ID_BASE,

  /** True for discussion channels that exist only in this file. */
  isSyntheticChannel: (channelUrl: string): boolean =>
    USE_ACTIVITY_FEED_FIXTURE && channelUrl.startsWith(SYNTHETIC_CHANNEL_PREFIX),

  /** Accepts an outbound referral source message without calling the API. */
  sendOutbound: (): Promise<void> => delay(undefined),

  /** Accepts a discussion reply, returning just enough to render it optimistically. */
  sendDiscussionReply: (): Promise<{ messageId: number; createdAt: number }> =>
    delay({ messageId: Date.now(), createdAt: Date.now() }),
}
